import React, { useState, useEffect } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { bikeBrandAPI, BASE_URL } from '../services/api';
import './BikeBrands.css';

function BikeBrands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    brandName: '',
    brandSlug: '',
    brandId: '',
    imgUrl: '',
    brandUrl: ''
  });
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [previewBrands, setPreviewBrands] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [syncingBrand, setSyncingBrand] = useState(null);

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');

  const fetchBrands = async (currentPage, currentLimit, currentSearch) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: currentLimit
      };
      if (currentSearch) params.search = currentSearch;

      const response = await bikeBrandAPI.getAll(params);

      if (Array.isArray(response.data)) {
        setBrands(response.data);
        setTotal(response.data.length);
        setTotalPages(1);
      } else {
        setBrands(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
      setError('');
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch bike brands');
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands(page, limit, search);
  }, [page, limit, search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'brandName') {
      const slug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      setFormData((prev) => ({ ...prev, brandName: value, brandSlug: slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingBrand) {
        await bikeBrandAPI.update(editingBrand.id, formData);
      } else {
        await bikeBrandAPI.create(formData);
      }

      resetForm();
      fetchBrands(page, limit, search);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save bike brand');
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      brandName: brand.brandName,
      brandSlug: brand.brandSlug,
      brandId: brand.brandId || '',
      imgUrl: brand.imgUrl || '',
      brandUrl: brand.brandUrl || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bike brand?')) {
      try {
        await bikeBrandAPI.delete(id);
        fetchBrands(page, limit, search);
      } catch (err) {
        setError('Failed to delete bike brand');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      brandName: '',
      brandSlug: '',
      brandId: '',
      imgUrl: '',
      brandUrl: ''
    });
    setEditingBrand(null);
    setShowForm(false);
  };

  const handleSync = async () => {
    setShowSyncModal(true);
    setLoadingPreview(true);
    setSyncError('');
    setPreviewBrands([]);

    try {
      const response = await bikeBrandAPI.preview();
      setPreviewBrands(response.data.brands || []);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to fetch brands from BikeDekho');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSyncSingle = async (brand) => {
    setSyncingBrand(brand.brandSlug);
    try {
      await bikeBrandAPI.syncSingle({
        brandName: brand.brandName,
        brandSlug: brand.brandSlug,
        brandId: brand.brandId,
        imgUrl: brand.imgUrl,
        brandUrl: brand.brandUrl
      });
      // Remove the brand from preview list after syncing
      setPreviewBrands(prev => prev.filter(b => b.brandSlug !== brand.brandSlug));
      // Refresh the main brands list
      fetchBrands(page, limit, search);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to sync brand');
    } finally {
      setSyncingBrand(null);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await bikeBrandAPI.sync();
      // Refresh preview to show all as existing
      const response = await bikeBrandAPI.preview();
      setPreviewBrands(response.data.brands || []);
      setPage(1);
      fetchBrands(1, limit, search);
    } catch (err) {
      setSyncError(err.response?.data?.error || 'Failed to sync all brands');
    } finally {
      setSyncing(false);
    }
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
    setPreviewBrands([]);
    setSyncError('');
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Sync Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sync Bike Brand</h2>
              <button className="modal-close" onClick={closeSyncModal}>&times;</button>
            </div>
            <div className="modal-body sync-modal-body">
              {loadingPreview && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p>Loading brands from BikeDekho...</p>
                </div>
              )}
              {syncError && (
                <div className="sync-error">
                  <div className="error-icon">&#10005;</div>
                  <h3>Error</h3>
                  <p>{syncError}</p>
                </div>
              )}
              {!loadingPreview && !syncError && previewBrands.length > 0 && (
                <div className="preview-brands-grid">
                  {previewBrands.map((brand) => (
                    <div key={brand.brandSlug} className="preview-brand-card">
                      <div className="preview-brand-image">
                        {brand.imgUrl ? (
                          <img
                            src={brand.imgUrl}
                            alt={brand.brandName}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="preview-logo-placeholder">
                            {brand.brandName.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="preview-brand-info">
                        <h4>{brand.brandName}</h4>
                      </div>
                      <button
                        className={`btn btn-sm ${brand.existsInDb ? 'btn-update' : 'btn-add'}`}
                        onClick={() => handleSyncSingle(brand)}
                        disabled={syncingBrand === brand.brandSlug}
                      >
                        {syncingBrand === brand.brandSlug ? 'Syncing...' : (brand.existsInDb ? 'Update' : 'Add')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {!loadingPreview && !syncError && previewBrands.length === 0 && (
                <div className="empty-state">
                  <p>No brands available to sync</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeSyncModal}>
                Close
              </button>
              <button
                className="btn btn-sync"
                onClick={handleSyncAll}
                disabled={syncing || loadingPreview || previewBrands.length === 0}
              >
                {syncing ? 'Syncing All...' : 'Sync All'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Bike Brands</h1>
        <div className="header-actions">
          <button
            className="btn btn-sync"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Data'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ Add New Brand'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {showForm && (
        <div className="card form-card">
          <h2>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Brand Name *</label>
                <input
                  type="text"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleChange}
                  placeholder="Enter brand name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Brand Slug *</label>
                <input
                  type="text"
                  name="brandSlug"
                  value={formData.brandSlug}
                  onChange={handleChange}
                  placeholder="auto-generated-slug"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Brand ID</label>
                <input
                  type="number"
                  name="brandId"
                  value={formData.brandId}
                  onChange={handleChange}
                  placeholder="Brand ID from BikeDekho"
                />
              </div>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="text"
                  name="imgUrl"
                  value={formData.imgUrl}
                  onChange={handleChange}
                  placeholder="/images/bike-brands/brand.jpg"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Brand URL</label>
                <input
                  type="text"
                  name="brandUrl"
                  value={formData.brandUrl}
                  onChange={handleChange}
                  placeholder="/brand-bikes"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingBrand ? 'Update Brand' : 'Create Brand'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input
              type="text"
              placeholder="Search by brand name..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          <div className="table-options">
            <label>
              Show
              <select value={limit} onChange={handleLimitChange}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              entries
            </label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : brands.length === 0 ? (
          <div className="empty-state">
            <p>No bike brands found. {search ? 'Try adjusting your filters.' : 'Add your first brand!'}</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Logo</th>
                  <th>Brand Name</th>
                  <th>Slug</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {brands.map((brand) => (
                  <tr key={brand.id}>
                    <td>{brand.id}</td>
                    <td>
                      <div className="brand-logo">
                        {brand.imgUrl ? (
                          <img
                            src={brand.imgUrl.startsWith('/') ? `${BASE_URL}${brand.imgUrl}` : brand.imgUrl}
                            alt={brand.brandName}
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div className="logo-placeholder" style={{ display: brand.imgUrl ? 'none' : 'flex' }}>
                          {brand.brandName.charAt(0)}
                        </div>
                      </div>
                    </td>
                    <td>{brand.brandName}</td>
                    <td><code>{brand.brandSlug}</code></td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-edit"
                          onClick={() => handleEdit(brand)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(brand.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>
    </div>
  );
}

export default BikeBrands;
